import { Check } from 'lucide-react';
import { useState } from 'react';

import type { EscritorioMembro } from '@/api/client';
import type { Equipe } from '@/api/equipes';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { CORES_TAG } from '@/lib/tags/cores';
import { cn } from '@/lib/utils';

import { Avatar } from './papeis';

export interface DadosDaEquipe {
  nome: string;
  cor: string | null;
  membro_ids: string[];
}

interface EquipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sem `equipe`, é a criação. */
  equipe?: Equipe;
  membros: EscritorioMembro[];
  salvando: boolean;
  onSalvar: (dados: DadosDaEquipe) => void;
}

/** Nome, cor (a paleta das tags) e quem está na equipe — uma pessoa pode estar em várias (R6). */
export function EquipeDialog(props: EquipeDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Montado a cada abertura: o formulário nasce da equipe atual, sem efeito. */}
        {props.open && <Formulario {...props} />}
      </DialogContent>
    </Dialog>
  );
}

function Formulario({ onOpenChange, equipe, membros, salvando, onSalvar }: EquipeDialogProps) {
  const [nome, setNome] = useState(equipe?.nome ?? '');
  const [cor, setCor] = useState<string | null>(equipe?.cor ?? null);
  const [selecionados, setSelecionados] = useState<Set<string>>(
    () => new Set(equipe?.membros.map((m) => m.usuario_id) ?? []),
  );

  const alternar = (usuarioId: string) =>
    setSelecionados((atual) => {
      const novo = new Set(atual);
      if (novo.has(usuarioId)) novo.delete(usuarioId);
      else novo.add(usuarioId);
      return novo;
    });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{equipe ? 'Editar equipe' : 'Nova equipe'}</DialogTitle>
        <DialogDescription>
          A equipe é um grupo de colegas para compartilhar de uma vez — por exemplo, &ldquo;Trabalhista&rdquo;.
        </DialogDescription>
      </DialogHeader>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (nome.trim()) onSalvar({ nome: nome.trim(), cor, membro_ids: [...selecionados] });
        }}
      >
        <div className="space-y-2">
          <Label htmlFor="equipe-nome">Nome</Label>
          <Input
            id="equipe-nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            maxLength={80}
            placeholder="Ex.: Cível"
            autoFocus
          />
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Cor</legend>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={cor === null}
              onClick={() => setCor(null)}
              className={cn('h-9', cor === null && 'border-primary')}
            >
              Sem cor
            </Button>
            {CORES_TAG.map((c) => (
              <Button
                key={c.hex}
                type="button"
                variant="outline"
                size="icon"
                aria-label={c.nome}
                aria-pressed={cor === c.hex}
                onClick={() => setCor(c.hex)}
                className={cn('h-9 w-9 rounded-full p-0', cor === c.hex && 'ring-2 ring-primary ring-offset-2')}
                style={{ backgroundColor: c.hex }}
              >
                {cor === c.hex && <Check className="h-4 w-4 text-gray-900" aria-hidden="true" />}
              </Button>
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">
            Membros <span className="font-normal text-muted-foreground">({selecionados.size})</span>
          </legend>
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-1">
            {membros.map((m) => {
              const marcado = selecionados.has(m.usuario_id);
              return (
                <Button
                  key={m.usuario_id}
                  type="button"
                  variant="ghost"
                  role="checkbox"
                  aria-checked={marcado}
                  onClick={() => alternar(m.usuario_id)}
                  className="h-auto min-h-11 w-full justify-start gap-3 px-2 py-1.5 font-normal"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                      marcado ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                    )}
                  >
                    {marcado && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <Avatar nome={m.usuario_nome} className="h-7 w-7 text-[11px]" />
                  <span className="min-w-0 truncate text-sm">{m.usuario_nome}</span>
                </Button>
              );
            })}
          </div>
        </fieldset>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" disabled={salvando || !nome.trim()}>
            {salvando && <Spinner size={16} className="mr-2 text-inherit" />}
            {equipe ? 'Salvar' : 'Criar equipe'}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
